import { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCreateOwner } from '@/hooks/useOwners';
import { useCreatePatient } from '@/hooks/usePatients';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { User, Dog, Plus, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

// Schema para una mascota
const patientSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  species: z.string().min(1, 'La especie es requerida'),
  breed: z.string().optional(),
  sex: z.enum(['male', 'female', 'unknown']),
  age_years: z.union([z.string(), z.number()]).optional(),
  age_months: z.union([z.string(), z.number()]).optional(),
  weight: z.union([z.string(), z.number()]).optional(),
  color: z.string().optional(),
});

const ownerSchema = z.object({
  // Datos del propietario
  first_name: z.string().min(1, 'El nombre es requerido'),
  last_name: z.string().min(1, 'El apellido es requerido'),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
  address: z.string().optional(),
  // Lista de mascotas
  patients: z.array(patientSchema).optional(),
});

type OwnerFormData = z.infer<typeof ownerSchema>;

interface OwnerWithPatientFormProps {
  onSuccess: () => void;
}

const speciesOptions = [
  { value: 'Canino', label: 'Canino' },
  { value: 'Felino', label: 'Felino' },
  { value: 'Otro', label: 'Otro' },
];

const sexOptions = [
  { value: 'male', label: 'Macho' },
  { value: 'female', label: 'Hembra' },
  { value: 'unknown', label: 'No especificado' },
];

// Función para calcular fecha de nacimiento desde edad
function calculateBirthDate(years?: number, months?: number): string | undefined {
  if (!years && !months) return undefined;
  const today = new Date();
  const birthDate = new Date(today);
  birthDate.setFullYear(birthDate.getFullYear() - (years || 0));
  birthDate.setMonth(birthDate.getMonth() - (months || 0));
  return birthDate.toISOString().split('T')[0];
}

export default function OwnerWithPatientForm({ onSuccess }: OwnerWithPatientFormProps) {
  const [showMoreOwnerFields, setShowMoreOwnerFields] = useState(false);
  const [expandedPatients, setExpandedPatients] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const createOwner = useCreateOwner();
  const createPatient = useCreatePatient();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<OwnerFormData>({
    resolver: zodResolver(ownerSchema),
    defaultValues: {
      patients: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'patients',
  });

  const addPatient = () => {
    append({
      name: '',
      species: 'Perro',
      breed: '',
      sex: 'unknown',
      age_years: '',
      age_months: '',
      weight: '',
      color: '',
    });
  };

  const togglePatientExpanded = (index: number) => {
    setExpandedPatients((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const onSubmit = async (data: OwnerFormData) => {
    setError(null);

    try {
      // 1. Crear el propietario
      const ownerData = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        address: data.address || undefined,
      };

      const newOwner = await createOwner.mutateAsync(ownerData);

      // 2. Crear las mascotas
      if (data.patients && data.patients.length > 0) {
        for (const patient of data.patients) {
          const birthDate = calculateBirthDate(
            patient.age_years ? Number(patient.age_years) : undefined,
            patient.age_months ? Number(patient.age_months) : undefined
          );

          const patientData = {
            owner_id: newOwner.id,
            name: patient.name,
            species: patient.species,
            breed: patient.breed || undefined,
            sex: patient.sex || 'unknown',
            birth_date: birthDate,
            weight: patient.weight ? Number(patient.weight) : undefined,
            color: patient.color || undefined,
          };

          await createPatient.mutateAsync(patientData);
        }
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error creating owner:', err);
      setError(err?.response?.data?.detail || 'Error al crear el registro');
    }
  };

  const isLoading = createOwner.isPending || createPatient.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Sección Propietario */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b">
          <User className="h-5 w-5 text-taupe-600" />
          <h3 className="font-semibold text-gray-900">Datos del Propietario</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            id="first_name"
            label="Nombre *"
            placeholder="Nombre del propietario"
            {...register('first_name')}
            error={errors.first_name?.message}
          />
          <Input
            id="last_name"
            label="Apellido *"
            placeholder="Apellido del propietario"
            {...register('last_name')}
            error={errors.last_name?.message}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            id="phone"
            label="Teléfono"
            placeholder="Número de contacto"
            {...register('phone')}
            error={errors.phone?.message}
          />
          <Input
            id="email"
            type="email"
            label="Email"
            placeholder="correo@ejemplo.com"
            {...register('email')}
            error={errors.email?.message}
          />
        </div>

        {/* Campos adicionales colapsables */}
        <button
          type="button"
          onClick={() => setShowMoreOwnerFields(!showMoreOwnerFields)}
          className="flex items-center gap-1 text-sm text-taupe-600 hover:text-taupe-800"
        >
          {showMoreOwnerFields ? (
            <>
              <ChevronUp className="h-4 w-4" />
              Ocultar campos adicionales
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              Mostrar más campos (Dirección)
            </>
          )}
        </button>

        {showMoreOwnerFields && (
          <div className="space-y-4 pt-2">
            <Input
              id="address"
              label="Dirección"
              placeholder="Dirección del propietario"
              {...register('address')}
              error={errors.address?.message}
            />
          </div>
        )}
      </div>

      {/* Sección Mascotas */}
      <div className="space-y-4">
        <div className="flex items-center justify-between pb-2 border-b">
          <div className="flex items-center gap-2">
            <Dog className="h-5 w-5 text-cream-700" />
            <h3 className="font-semibold text-gray-900">Mascotas</h3>
            {fields.length > 0 && (
              <span className="text-sm text-gray-500">({fields.length})</span>
            )}
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={addPatient}>
            <Plus className="h-4 w-4 mr-1" />
            Agregar Mascota
          </Button>
        </div>

        {fields.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <Dog className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No hay mascotas agregadas</p>
            <p className="text-xs text-gray-400 mt-1">
              Puedes agregar mascotas ahora o después
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addPatient}
              className="mt-3"
            >
              <Plus className="h-4 w-4 mr-1" />
              Agregar primera mascota
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="border rounded-xl p-4 bg-cream-50 space-y-4"
              >
                {/* Header de la mascota */}
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">
                    Mascota {index + 1}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {/* Campos principales */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    id={`patients.${index}.name`}
                    label="Nombre *"
                    placeholder="Nombre de la mascota"
                    {...register(`patients.${index}.name`)}
                    error={errors.patients?.[index]?.name?.message}
                  />
                  <Select
                    id={`patients.${index}.species`}
                    label="Especie *"
                    options={speciesOptions}
                    {...register(`patients.${index}.species`)}
                    error={errors.patients?.[index]?.species?.message}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    id={`patients.${index}.breed`}
                    label="Raza"
                    placeholder="Ej: Labrador, Siamés..."
                    {...register(`patients.${index}.breed`)}
                  />
                  <Select
                    id={`patients.${index}.sex`}
                    label="Sexo"
                    options={sexOptions}
                    {...register(`patients.${index}.sex`)}
                  />
                </div>

                {/* Edad */}
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    id={`patients.${index}.age_years`}
                    type="number"
                    min="0"
                    label="Edad (años)"
                    placeholder="0"
                    {...register(`patients.${index}.age_years`)}
                  />
                  <Input
                    id={`patients.${index}.age_months`}
                    type="number"
                    min="0"
                    max="11"
                    label="Meses"
                    placeholder="0"
                    {...register(`patients.${index}.age_months`)}
                  />
                </div>

                {/* Campos adicionales colapsables */}
                <button
                  type="button"
                  onClick={() => togglePatientExpanded(index)}
                  className="flex items-center gap-1 text-sm text-taupe-600 hover:text-taupe-800"
                >
                  {expandedPatients.includes(index) ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      Ocultar campos adicionales
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      Más campos (peso, color)
                    </>
                  )}
                </button>

                {expandedPatients.includes(index) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <Input
                      id={`patients.${index}.weight`}
                      type="number"
                      step="0.1"
                      label="Peso (kg)"
                      placeholder="Ej: 5.5"
                      {...register(`patients.${index}.weight`)}
                    />
                    <Input
                      id={`patients.${index}.color`}
                      label="Color"
                      placeholder="Ej: Negro, Blanco..."
                      {...register(`patients.${index}.color`)}
                    />
                  </div>
                )}
              </div>
            ))}

            {/* Botón para agregar más mascotas */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addPatient}
              className="w-full border-2 border-dashed"
            >
              <Plus className="h-4 w-4 mr-1" />
              Agregar otra mascota
            </Button>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="submit" loading={isLoading}>
          <Plus className="h-4 w-4 mr-2" />
          {fields.length > 0
            ? `Crear Propietario y ${fields.length} mascota${fields.length > 1 ? 's' : ''}`
            : 'Crear Propietario'}
        </Button>
      </div>
    </form>
  );
}
